import * as THREE from 'three';
import { CONFIG } from '../config.js';
import { DigitalObject } from '../rendering/binary/DigitalObject.js';
import { findPath } from './NPCNavigation.js';
import { NPCPersonality } from './NPCPersonality.js';
import { FootstepController } from '../player/FootstepController.js';

export const NPC_STATE = {
  IDLE: 'IDLE',
  WALKING: 'WALKING',
  WAITING: 'WAITING',
  CROSSING_ROAD: 'CROSSING_ROAD',
  AVOIDING_NPC: 'AVOIDING_NPC',
  AVOIDING_PLAYER: 'AVOIDING_PLAYER',
  LOOKING_AROUND: 'LOOKING_AROUND',
};

/**
 * Digital pedestrian agent with personality-driven FSM.
 */
export class NPC {
  /**
   * @param {{
   *   id: number;
   *   material: import('../rendering/binary/BinaryMaterial.js').BinaryMaterial;
   *   graph: import('./NavigationGraph.js').NavigationGraph;
   *   cellIdBase: number;
   *   seed: number;
   *   config: import('../config.js').CONFIG['npc'];
   * }} options
   */
  constructor({ id, material, graph, cellIdBase, seed, config }) {
    this.id = id;
    this.graph = graph;
    this.config = config;
    this.personality = new NPCPersonality(seed, config);

    this.position = new THREE.Vector3();
    this.rotation = 0;
    this.speed = 1.6;
    this.state = NPC_STATE.IDLE;
    /** @type {number[]} */
    this.path = [];
    this.pathIndex = 0;
    this.destinationNodeId = -1;
    this.currentNodeId = 0;
    this.waitTimer = 0;
    this.stuckTimer = 0;
    this.active = false;
    this.updatePhase = id % 3;
    this._rngSeed = seed;
    this._pathfindCooldown = 0;
    this._staticHidden = false;
    this._steer = new THREE.Vector3();
    this._animTime = 0;
    this._walkAnim = 0;
    /** @type {((npc: NPC, event: import('../player/FootstepController.js').FootstepEvent) => void) | null} */
    this._footstepHandler = null;
    const npcStep = CONFIG.audio?.npcFootsteps;
    this.footsteps = new FootstepController({
      minSpeed: npcStep?.minSpeed ?? 0.35,
      cadence: npcStep?.cadence ?? 2.85,
      onFootstep: (event) => this._footstepHandler?.(this, event),
    });

    this.root = new THREE.Group();
    this.root.name = 'NPC';

    this.body = new DigitalObject({
      material,
      shape: 'facade',
      size: [0.42, 1.72, 0.32],
      position: [0, 0.86, 0],
      cellsPerMeter: 5,
      seed,
      cellIdBase,
      name: 'NPCBody',
    });
    this.root.add(this.body.group);
  }

  /** @param {((npc: NPC, event: import('../player/FootstepController.js').FootstepEvent) => void) | null} handler */
  setFootstepHandler(handler) {
    this._footstepHandler = handler;
  }

  spawn(x, z, speed, startNodeId) {
    this.position.set(x, 0, z);
    this.speed = speed;
    this.active = true;
    this.stuckTimer = 0;
    this.currentNodeId = startNodeId;
    this.state = NPC_STATE.IDLE;
    this.waitTimer =
      0.2 + this.personality.timerJitter * 0.15 + (this._hash() % 100) / 400;
    this.destinationNodeId = startNodeId;
    this.path = [];
    this.root.visible = true;
    this._syncTransform();
  }

  /**
   * Distant LOD — idle sway only (no pathfinding).
   * @param {number} delta
   */
  updateSimplified(delta) {
    if (!this.active) return;
    this._animTime += delta;
    this._walkAnim += delta * 2.2;
    if (!this._staticHidden) {
      this.root.visible = true;
    }
    this.waitTimer -= delta;
    if (this.waitTimer <= 0) {
      this.waitTimer = 1.2 + this.personality.timerJitter + (this._hash() % 100) / 80;
      if (this.state !== NPC_STATE.LOOKING_AROUND && this._hash() % 100 > 70) {
        this.state = NPC_STATE.LOOKING_AROUND;
        this.rotation += (this._hash() % 100) / 100 - 0.5;
      } else {
        this.state = NPC_STATE.WAITING;
      }
    }
    this._syncTransform();
  }

  /**
   * Medium distance — follow path, no avoidance / repath (cheap).
   * @param {number} delta
   */
  updateReduced(delta) {
    if (!this.active) return;
    this.root.visible = true;
    this._staticHidden = false;
    this._pathfindCooldown = Math.max(0, this._pathfindCooldown - delta);

    if (this.path.length === 0) {
      this.waitTimer -= delta;
      this._syncTransform();
      return;
    }

    const target = this.graph.getNode(this.path[this.pathIndex]);
    if (!target) {
      this._syncTransform();
      return;
    }

    const dx = target.x - this.position.x;
    const dz = target.z - this.position.z;
    const dist = Math.hypot(dx, dz);
    if (dist < 0.28) {
      this.currentNodeId = target.id;
      this._advancePathOrWait();
      this._syncTransform();
      return;
    }

    const prevX = this.position.x;
    const prevZ = this.position.z;
    const step = this.speed * delta * 0.92;
    const move = Math.min(step, dist);
    this.position.x += (dx / dist) * move;
    this.position.z += (dz / dist) * move;
    this.rotation = Math.atan2(dx, dz);
    const horizontalSpeed =
      Math.hypot(this.position.x - prevX, this.position.z - prevZ) /
      Math.max(delta, 0.0001);
    this._emitFootsteps(delta, horizontalSpeed, true);
    this._syncTransform();
  }

  /**
   * Very far — hidden, occasional idle tick.
   * @param {number} delta
   * @param {number} framePhase
   */
  updateStatic(delta, framePhase) {
    if (!this.active) return;
    if ((framePhase + this.id) % 16 !== 0) return;
    this.root.visible = false;
    this._staticHidden = true;
    this.waitTimer -= delta * 8;
  }

  deactivate() {
    this.active = false;
    this.path = [];
    this.personality.clearGroup();
    this.root.visible = false;
  }

  /**
   * @param {number} delta
   * @param {import('./NPCSpawner.js').NPCSpawner} spawner
   * @param {THREE.Vector3} playerPosition
   * @param {NPC[]} neighbors
   * @param {number} framePhase
   */
  update(delta, spawner, playerPosition, neighbors, framePhase = 0) {
    if (!this.active) return;
    this._animTime += delta;
    const walkRate =
      this.state === NPC_STATE.WALKING ? 9.5 + this.speed * 0.35 : 2.2;
    this._walkAnim += delta * walkRate;
    this.root.visible = true;
    this._staticHidden = false;
    this._pathfindCooldown = Math.max(0, this._pathfindCooldown - delta);

    const heavyThink =
      (framePhase + this.updatePhase) % this.config.thinkStride === 0;

    if (this.state === NPC_STATE.LOOKING_AROUND) {
      this.waitTimer -= delta;
      if (this.waitTimer <= 0) {
        this.state = NPC_STATE.WALKING;
      }
      this._syncTransform();
      return;
    }

    if (
      this.state === NPC_STATE.WAITING ||
      this.state === NPC_STATE.IDLE ||
      this.state === NPC_STATE.CROSSING_ROAD
    ) {
      this.waitTimer -= delta;
      if (this.waitTimer <= 0) {
        if (this.state === NPC_STATE.CROSSING_ROAD) {
          this.state = NPC_STATE.WALKING;
          this._advancePathOrWait();
        } else if (heavyThink || this.path.length === 0) {
          this._assignNewPath(spawner);
        }
      }
      this._syncTransform();
      return;
    }

    if (this.path.length === 0) {
      this.state = NPC_STATE.WAITING;
      this.waitTimer = this.personality.destinationWait(this.config);
      this._syncTransform();
      return;
    }

    const target = this.graph.getNode(this.path[this.pathIndex]);
    if (!target) {
      this.state = NPC_STATE.WAITING;
      this.waitTimer = 0.4 + this.personality.timerJitter * 0.2;
      this._syncTransform();
      return;
    }

    const dx = target.x - this.position.x;
    const dz = target.z - this.position.z;
    const dist = Math.hypot(dx, dz);

    let speedScale = 1;
    let avoidNpc = false;
    let avoidPlayer = false;
    this._steer.set(0, 0, 0);

    for (const other of neighbors) {
      if (other === this || !other.active) continue;
      const ox = this.position.x - other.position.x;
      const oz = this.position.z - other.position.z;
      const d = Math.hypot(ox, oz);
      if (d < 0.01 || d > this.config.avoidanceRadius * 1.5) continue;
      if (d < this.config.avoidanceRadius) {
        const push = (this.config.avoidanceRadius - d) / this.config.avoidanceRadius;
        this._steer.x += (ox / d) * push;
        this._steer.z += (oz / d) * push;
        speedScale = Math.min(speedScale, 0.35 + (d / this.config.avoidanceRadius) * 0.55);
        avoidNpc = true;
      }
    }

    const pdx = this.position.x - playerPosition.x;
    const pdz = this.position.z - playerPosition.z;
    const playerDist = Math.hypot(pdx, pdz);
    if (playerDist < this.config.playerAvoidRadius && playerDist > 0.01) {
      const push = (this.config.playerAvoidRadius - playerDist) / this.config.playerAvoidRadius;
      this._steer.x += (pdx / playerDist) * push * 0.85;
      this._steer.z += (pdz / playerDist) * push * 0.85;
      speedScale = Math.min(speedScale, 0.55 + (playerDist / this.config.playerAvoidRadius) * 0.35);
      avoidPlayer = true;
    }

    if (avoidPlayer) {
      this.state = NPC_STATE.AVOIDING_PLAYER;
    } else if (avoidNpc) {
      this.state = NPC_STATE.AVOIDING_NPC;
    } else if (target.kind === 'intersection' && dist < 1.2) {
      this.state = NPC_STATE.CROSSING_ROAD;
    } else {
      this.state = NPC_STATE.WALKING;
    }

    const prevX = this.position.x;
    const prevZ = this.position.z;

    if (dist < 0.28) {
      this.currentNodeId = target.id;
      if (target.kind === 'intersection') {
        this.state = NPC_STATE.CROSSING_ROAD;
        this.waitTimer = this.personality.intersectionWait(this.config);
        this._syncTransform();
        return;
      }
      if (
        heavyThink &&
        this._hash() % 100 < this.personality.lookAroundChance * 100
      ) {
        this.state = NPC_STATE.LOOKING_AROUND;
        this.waitTimer = 0.35 + (this._hash() % 100) / 120;
        this.pathIndex += 1;
        this._syncTransform();
        return;
      }
      this._advancePathOrWait();
      this._syncTransform();
      return;
    }

    if (speedScale <= 0.05) {
      this.stuckTimer += delta;
      if (this.stuckTimer > this.config.stuckTimeout && heavyThink) {
        this._assignNewPath(spawner);
      }
      this._syncTransform();
      return;
    }

    let dirX = dx / dist;
    let dirZ = dz / dist;
    if (this._steer.lengthSq() > 0.0001) {
      dirX += this._steer.x * 0.55;
      dirZ += this._steer.z * 0.55;
      const len = Math.hypot(dirX, dirZ) || 1;
      dirX /= len;
      dirZ /= len;
    }

    const step = this.speed * speedScale * delta;
    const move = Math.min(step, dist);
    this.position.x += dirX * move;
    this.position.z += dirZ * move;
    this.rotation = Math.atan2(dirX, dirZ);

    if (Math.hypot(this.position.x - prevX, this.position.z - prevZ) < 0.012) {
      this.stuckTimer += delta;
    } else {
      this.stuckTimer = 0;
    }

    if (this.stuckTimer > this.config.stuckTimeout && heavyThink) {
      this._assignNewPath(spawner);
    }

    const horizontalSpeed =
      Math.hypot(this.position.x - prevX, this.position.z - prevZ) /
      Math.max(delta, 0.0001);
    this._emitFootsteps(delta, horizontalSpeed);
    this._syncTransform();
  }

  /**
   * Follow a group leader's path (called by manager).
   * @param {NPC} leader
   */
  followLeader(leader) {
    if (!leader.active || leader.path.length === 0) return;
    this.path = leader.path.slice();
    this.pathIndex = Math.min(leader.pathIndex, this.path.length - 1);
    this.destinationNodeId = leader.destinationNodeId;
    this.state = NPC_STATE.WALKING;
    this.stuckTimer = 0;
  }

  _advancePathOrWait() {
    if (this.pathIndex >= this.path.length - 1) {
      this.state = NPC_STATE.WAITING;
      this.waitTimer = this.personality.destinationWait(this.config);
      this.path = [];
      return;
    }
    this.pathIndex += 1;
  }

  /** @param {import('./NPCSpawner.js').NPCSpawner} spawner */
  _assignNewPath(spawner) {
    const cooldown =
      CONFIG.performance?.pathfindCooldownSeconds ?? 0.45;
    if (this._pathfindCooldown > 0) return;

    const fromNode = this.currentNodeId;
    const dest = spawner.pickDestination(fromNode, this.personality);

    this.path = findPath(this.graph, fromNode, dest);
    this.pathIndex = 0;
    this.destinationNodeId = dest;
    this.stuckTimer = 0;

    if (this.path.length === 0) {
      this.state = NPC_STATE.WAITING;
      this.waitTimer = 0.35 + this.personality.timerJitter * 0.25;
      return;
    }

    this.state = NPC_STATE.WALKING;
    this._pathfindCooldown = cooldown;
  }

  _hash() {
    this._rngSeed = (1664525 * this._rngSeed + 1013904223) >>> 0;
    return this._rngSeed;
  }

  /**
   * @param {number} delta
   * @param {number} horizontalSpeed
   * @param {boolean} [movingAlongPath]
   */
  _emitFootsteps(delta, horizontalSpeed, movingAlongPath = false) {
    const moving =
      movingAlongPath ||
      this.state === NPC_STATE.WALKING ||
      this.state === NPC_STATE.CROSSING_ROAD ||
      this.state === NPC_STATE.AVOIDING_NPC ||
      this.state === NPC_STATE.AVOIDING_PLAYER;
    if (!moving || !this._footstepHandler) return;
    this.footsteps.update({
      horizontalSpeed,
      onGround: true,
      sprinting: false,
      delta,
    });
  }

  _syncTransform() {
    this.root.position.copy(this.position);
    this.root.rotation.y = this.rotation;

    const bob =
      Math.sin(this._walkAnim) *
      (this.state === NPC_STATE.WALKING ? 0.045 : 0.018);
    const sway = Math.sin(this._walkAnim * 0.5) * 0.035;
    this.body.group.position.y = 0.86 + bob;
    this.body.group.rotation.z = sway;
    const scalePulse = 1 + Math.sin(this._animTime * 6) * 0.012;
    this.body.group.scale.set(1, scalePulse, 1);
  }

  dispose() {
    this.body.dispose();
    this.root.clear();
  }
}
